/**
 * The subset of design tokens antd needs as literal values.
 *
 * WHY THIS DUPLICATES tokens.css, WHICH IS OTHERWISE THE SINGLE SOURCE.
 * antd v5 does not merely apply a seed colour, it derives a whole palette from
 * it arithmetically — `colorPrimaryHover`, `colorPrimaryActive`, the ten-step
 * ramp behind them — by manipulating the colour's channels. It cannot do that
 * with `var(--color-ink-900)`, because at the point the algorithm runs the
 * string has no value. Passing a CSS variable yields a theme whose base colour
 * is right and whose every interaction state is broken.
 *
 * So the values live twice, and `tokens.sync.test.ts` asserts every value here
 * appears in tokens.css. Duplication that a test pins is a maintenance cost;
 * duplication that nothing pins is the two-config-modules problem this codebase
 * already had once, where both copies were live and fixing one left the other
 * wrong with no test able to tell.
 *
 * Values originate in ~/Downloads/Avrio AI Design System/tokens/colors.css.
 * Change them there, re-extract tokens.css, then update these to match — the
 * test will tell you if you miss one.
 */

export const token = {
  /** Primary text, dark surfaces, and primary button fill. */
  ink900: '#0D0F14',
  ink800: '#141822',
  /** Page background. */
  mist50: '#F6F8FA',
  white: '#FFFFFF',
  /**
   * Accent. The brand rules cap this at roughly 5% of any surface — buttons,
   * one word of a headline, active states. It is not a decoration colour, which
   * is why it is NOT antd's colorPrimary here: antd spends its primary on every
   * focus ring, link, selected row and active tab, which would put the accent
   * well past that cap without anyone choosing to.
   */
  accent600: '#4C4FF7',
  accent050: 'rgba(76,79,247,0.08)',
  border: '#E5E7EB',
  // See tokens.css: #9CA3AF failed WCAG AA for body text on both surfaces.
  gray600: '#6B7280',
  gray100: '#EEEEF1',
  success500: '#2BB656',
  teal500: '#07C4B2',
} as const;

/** 5px almost everywhere — the system specifies no other resting radius. */
export const radius = { sm: 4, md: 5, lg: 8 } as const;

export const fontFamily =
  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

/**
 * Semantic state colours, DELIBERATELY NOT PART OF `token` ABOVE.
 *
 * `token` mirrors tokens.css, which is extracted verbatim from the Avrio design
 * system, and tokens.sync.test.ts asserts the two agree. These two do not come
 * from the design system: it ships a success green and a teal for status dots
 * and nothing for "this went badly" or "this needs attention", because it was
 * built for marketing surfaces where those states do not arise.
 *
 * Putting them in `token` broke the sync test, which was correct of it — the
 * test exists to catch exactly this, a value in the antd layer that the system
 * does not define. They live here instead, so the guard stays sharp and the
 * provenance stays visible: everything in `token` is the system's, everything
 * here is ours and needs a reason.
 *
 * Their values come from the reference scorecard mockup, which carries the
 * semantic set the system lacks. See smallApps/jobs/utils/statusPalette.ts.
 */
export const semantic = {
  error: '#D64545',
  warning: '#C98A12',
} as const;
